/**
 * Install scheduling UI for CAP Read & Sign.
 */
import React, { useCallback, useState } from "react";
import { DateTime } from "luxon";
import { SiteSurveyAppointment } from "../../../../amplify/backend/function/OfferExpCheckout/ts/public/types";
import {
  POST_CAP_INSTALL_TEST_IDS,
  type SiteSurveySlotPickerWarning,
} from "../../../checkout/components/molecules/siteSurveyScheduling";
import { InstallCustomerConfirmModal } from "../molecules/InstallCustomerConfirmModal";
import {
  getInstallScheduledStatusPill,
  POST_CAP_INSTALL_EMPTY_SLOTS_MESSAGE,
  POST_CAP_INSTALL_LOAD_SLOTS_BUTTON,
  POST_CAP_INSTALL_SCHEDULED_ARRIVAL_INTRO_START_ONLY,
  POST_CAP_INSTALL_SCHEDULED_CARD_MAX_WIDTH,
} from "../../constants/capInstallScheduling";
import { useInstallAppointmentConfirmation } from "../../hooks/useInstallAppointmentConfirmation";
import { CapAppointmentScheduler } from "./CapAppointmentScheduler";
import { type SiteSurveySlot } from "checkout/api/siteSurvey";

type PostCapInstallSchedulerProps = {
  prospectId?: string;
  prospectTimeZoneId?: string;
  bookingSnapshotLoading: boolean;
  scheduledAppointment: SiteSurveyAppointment | undefined;
  slotBookingParentId: string | undefined;
  slots: SiteSurveySlot[] | undefined;
  slotsLoading: boolean;
  slotsError: unknown;
  retrySlots: () => void;
  onLoadSlots?: () => void;
  loadSlotsLabel?: string;
  bookingLoading: boolean;
  rescheduleLoading: boolean;
  bookSlot: (slot: SiteSurveySlot) => Promise<unknown>;
  rescheduleAppointment: () => Promise<unknown>;
  daysOut: number;
  pickerResetKey?: string | number;
  canSchedule?: boolean;
  emptySlotsMessage?: string;
  slotPickerWarning?: SiteSurveySlotPickerWarning;
  showStartTimeOnly?: boolean;
};

const PostCapInstallScheduler: React.FC<PostCapInstallSchedulerProps> = ({
  prospectId,
  daysOut,
  prospectTimeZoneId,
  pickerResetKey,
  bookSlot,
  scheduledAppointment,
  emptySlotsMessage = POST_CAP_INSTALL_EMPTY_SLOTS_MESSAGE,
  showStartTimeOnly = false,
  loadSlotsLabel = POST_CAP_INSTALL_LOAD_SLOTS_BUTTON,
  ...props
}) => {
  const [showCustomerConfirmModal, setShowCustomerConfirmModal] =
    useState(false);

  const bookSlotWithCustomerConfirmModal = useCallback(
    (slot: SiteSurveySlot) =>
      bookSlot(slot).then(() => {
        setShowCustomerConfirmModal(true);
      }),
    [bookSlot]
  );

  const {
    isCustomerConfirmed,
    isPollingExpired,
    isRefreshing,
    refreshConfirmation,
  } = useInstallAppointmentConfirmation(
    prospectId,
    scheduledAppointment?.appointmentId
  );

  const scheduledStatusPill = scheduledAppointment
    ? getInstallScheduledStatusPill(isCustomerConfirmed)
    : undefined;

  const confirmationRefresh =
    scheduledAppointment && isPollingExpired && !isCustomerConfirmed
      ? {
          show: true,
          isLoading: isRefreshing,
          onRefresh: refreshConfirmation,
          testId: POST_CAP_INSTALL_TEST_IDS.confirmationRefreshButton,
        }
      : undefined;

  const minSchedulableDate = (
    prospectTimeZoneId
      ? DateTime.now().setZone(prospectTimeZoneId)
      : DateTime.now()
  )
    .startOf("day")
    .plus({ days: daysOut });

  return (
    <>
      <CapAppointmentScheduler
        testIds={POST_CAP_INSTALL_TEST_IDS}
        emptySlotsMessage={emptySlotsMessage}
        minSchedulableDate={minSchedulableDate}
        pickerResetKey={pickerResetKey}
        scheduledStatusPill={scheduledStatusPill}
        scheduledCardMaxWidth={POST_CAP_INSTALL_SCHEDULED_CARD_MAX_WIDTH}
        showRescheduleButton={false}
        confirmationRefresh={confirmationRefresh}
        prospectTimeZoneId={prospectTimeZoneId}
        scheduledAppointment={scheduledAppointment}
        bookSlot={bookSlotWithCustomerConfirmModal}
        showStartTimeOnly={showStartTimeOnly}
        loadSlotsLabel={loadSlotsLabel}
        scheduledArrivalIntro={
          showStartTimeOnly
            ? POST_CAP_INSTALL_SCHEDULED_ARRIVAL_INTRO_START_ONLY
            : undefined
        }
        {...props}
      />
      <InstallCustomerConfirmModal
        open={showCustomerConfirmModal}
        onClose={() => setShowCustomerConfirmModal(false)}
      />
    </>
  );
};

export type { PostCapInstallSchedulerProps };
export { PostCapInstallScheduler };
